"use client";

import {
  forwardRef,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from "react";
import { useNavigate } from "@tanstack/react-router";
import { parseHref } from "./href";

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  href: string;
  prefetch?: boolean;
  replace?: boolean;
  children?: ReactNode;
};

/**
 * Drop-in for `next/link`. Client-navigates on plain left clicks; meta/ctrl
 * click, middle click, and modified clicks keep native behaviour.
 *
 * `prefetch` is accepted so TopNav/SubTabs keep the attribute the nav-speed
 * check pins. TanStack preloads on intent via the router anyway.
 */
const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { href, prefetch: _prefetch, replace, children, onClick, ...rest },
  ref,
) {
  const navigate = useNavigate();
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented) return;
    if (e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const target = rest.target;
    if (target && target !== "_self") return;
    if (href.startsWith("http://") || href.startsWith("https://") || href.startsWith("mailto:")) {
      return;
    }
    e.preventDefault();
    const { pathname, search, hash } = parseHref(href);
    void navigate({
      to: pathname as never,
      replace,
      hash,
      search: search as never,
    });
  };
  return (
    <a ref={ref} href={href} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
});

export default Link;
